import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession, } from '../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../app/Helpers';
import { BusinessEndpointClient } from '../../../../repository/UserManagement/Dimensions/BusinessServiceClientPb';
import { UpdateBusinessActivityRequest, GetBusinessesRequest, GetBusinessesResponse, UpdateBusinessActivityResponse, UpdateThirdPartyConnectionResponse, UpdateThirdPartyConnectionRequest, GetBusinessResponse, GetBusinessRequest, GenerateInvitationResponse, GenerateInvitationRequest } from '../../../../repository/UserManagement/Dimensions/business_pb';
import { DynamicField } from '../../../common/DynamicFields/DynamicFields';
import { UserEndpointClient } from '../../../../repository/UserManagement/Customers/UserServiceClientPb';
import { GetUsersRequest, GetUsersResponse } from '../../../../repository/UserManagement/Customers/user_pb';
import { TableState } from '../../../common/Table/TableSate';


export interface BusinessDetailsPageState {
    item: any
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    isLoading: boolean
    clients: TableState,

}

const initialState: BusinessDetailsPageState = {
    item: undefined,
    isChangeStateLoading: false,
    message: undefined,
    isLoading: false,
    clients: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },

}

const businessClient = new BusinessEndpointClient(API_URL, null, null);
const clienCustomers = new UserEndpointClient(API_URL, null, null);


export const getCustomers = createAsyncThunk<GetUsersResponse.AsObject, APIRequest<GetUsersRequest>, {
    rejectValue: ApiMessage
}>(
    'pages/businesses/businessDetails/clients/fetch',
    async (req, thunkApi) => {

        let callReq = async (): Promise<any> => {
            try {
                var r = await clienCustomers.getUsers(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {
                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)


export const get = createAsyncThunk<GetBusinessResponse.AsObject, APIRequest<GetBusinessRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/businesses/businessDetails/get',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await businessClient.getBusiness(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)


export const updateActivity = createAsyncThunk<APIResponse<UpdateBusinessActivityResponse.AsObject>, APIRequest<UpdateBusinessActivityRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/businesses/businessDetails/updateActivity',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await businessClient.updateActivity(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId(), state: req.body.getIsactive() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)

export const generateInvitationLink = createAsyncThunk<GenerateInvitationResponse.AsObject, APIRequest<GenerateInvitationRequest>, {
    rejectValue: ApiMessage
}>(
    'pages/businesses/businessDetails/generateInvitationLink',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await businessClient.generateInvitationLink(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {
                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)




export const businessDetailsPageSlice = createSlice({
    name: 'pages/businesses/businessDetails',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.clients = initialState.clients;

        },

        resetItem: (state) => {
            state.item = undefined;

        },
        resetUsers: (state) => {
            state.clients.items = [];
            state.clients.hasMore = true;
        },
        setSelectedUserItems: (state, action: PayloadAction<any[]>) => {
            state.clients.selected = action.payload;


        },
        setIsFilteredOwnersSet: (state, action: PayloadAction<boolean>) => {
            state.clients.isFilteredSet = action.payload;
        },
        updateItem: (state, action: PayloadAction<any>) => {
            state.item = action.payload;

        },

        updateConnection: (state, action: PayloadAction<any>) => {
            state.item.connectionId = action?.payload?.connectionId
            state.item.connectionMetaData = action?.payload?.connectionMetaData

        }
    },
    extraReducers: (builder) => {
        builder.addCase(getCustomers.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.usersList.map(val => {
                    return {
                        internalId: val.profile?.internalid,
                        name: (val.profile?.firstname?.value ?? "") + " " + (val.profile?.lastname?.value ?? ""),
                        firstname: (val.profile?.firstname?.value ?? ""),
                        lastname: (val.profile?.lastname?.value ?? ""),
                        username: val.username?.value ?? "",
                        isActive: val.isactive,
                        isConfirmed: val.isconfirmed,
                        createdDate: formatDate(timestampToDate(val.profile?.createddate?.seconds, val.profile?.createddate?.nanos)),
                        addedBy: (val.profile?.addedby?.firstname?.value ?? "") + " " + (val.profile?.addedby?.lastname?.value ?? ""),
                        addedById: val.profile?.addedby?.id?.value,
                        role: val.profile?.role?.name?.value,
                        roleId: val.profile?.role?.id,
                        usertypeId: val.profile?.usertype?.id,
                        usertype: val.profile?.usertype?.name?.value,
                        lastlogin: formatDate(timestampToDate(val.lastlogin?.seconds, val.lastlogin?.nanos)),
                        imageId: val.profile?.image?.id?.value,
                        imageExtension: val.profile?.image?.extension?.value,
                        imageName: val.profile?.image?.name?.value,
                        imageOriginalName: val.profile?.image?.originalname?.value,
                        imagePath: val.profile?.image?.path?.value,
                        imageSize: val.profile?.image?.size?.value ?? 0,
                        imageSource: val.profile?.image?.source?.value,

                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                if (r.length < state.clients.numberOfResults) {
                    state.clients.hasMore = false;
                }
                state.clients.items = state.clients.items.concat(r);
                //console.log(state.items)

            } else {
                state.clients.hasMore = false;
            }
            state.clients.isFetching = false

        })
        builder.addCase(getCustomers.rejected, (state, action) => {
            state.clients.isFetching = false;
            if (action.payload) {
                //state.message = action.payload;
            }
        })
        builder.addCase(getCustomers.pending, (state, action) => {
            state.clients.isFetching = true;
            state.message = undefined;
        })

        builder.addCase(generateInvitationLink.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;

            if (payload.success?.message) {
                var r = toApiMessage((payload.success?.message));;
                r.body = "https://sanadapp.ly/b/" + payload.success?.id?.value + "_" + payload.success?.signature?.value;
                r.data = 300
                state.message = r;
            }

        })
        builder.addCase(generateInvitationLink.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(generateInvitationLink.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })



        builder.addCase(updateActivity.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                state.item.isActive = payload.metadata?.state;
            }
        })
        builder.addCase(updateActivity.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(updateActivity.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })

        builder.addCase(get.fulfilled, (state, { payload }) => {
            state.isLoading = false
            if (payload) {

                let r = {
                    id: payload.success?.id,
                    name: (payload.success?.name?.value ?? ""),
                    connectionId: payload.success?.connection?.id?.value ?? "",
                    connectionMetaData: payload.success?.connection?.metadata?.value ?? "",
                    description: payload.success?.description?.value ?? "",
                    isActive: payload.success?.isactive?.value,
                    createdDate: formatDate(timestampToDate(payload.success?.createddate?.seconds, payload.success?.createddate?.nanos)),
                    createdBy: (payload.success?.createdby?.firstname?.value ?? "") + " " + (payload.success?.createdby?.lastname?.value ?? ""),
                    createdById: payload.success?.createdby?.id?.value,
                    isAvailable: payload.success?.isavailable?.value,
                    businessType: payload.success?.businesstype?.value,
                    maxNumberOfAccountsAllowed: payload.success?.maxnumberofaccountsallowed?.value ?? 0,
                    registrationUrl: payload.success?.registrationurl?.value ?? '',
                    location: payload.success?.locationList,
                    addressCity: payload.success?.address?.city?.value,
                    addressCountry: payload?.success?.address ? { key: payload?.success?.address?.countryiso?.value, text: '' } : { key: 'LY', text: 'Libya' },
                    logoId: payload.success?.logo?.id?.value,
                    logoName: payload.success?.logo?.name?.value,
                    logoPath: payload.success?.logo?.path?.value,
                    logoSource: payload.success?.logo?.source?.value,
                    extraFields: [] as DynamicField[],
                    phoneNumbers: [] as DynamicField[],
                    urls: [] as DynamicField[],
                    long: payload.success?.locationList?.length == 2 ? payload?.success?.locationList[0] : "",
                    lat: payload.success?.locationList?.length == 2 ? payload?.success?.locationList[1] : "",
                    addressFirstLine: payload.success?.address ? payload?.success?.address?.firstline?.value : "",
                    addressSecondLine: payload.success?.address ? payload?.success?.address?.secondline?.value : "",
                    addressPostalCode: payload.success?.address ? payload.success?.address?.postalcode?.value : "",


                }

                if (payload.success?.phonenumbersList) {
                    let count = 0
                    payload.success?.phonenumbersList?.forEach(element => {
                        r.phoneNumbers.push({ key: count + "", label: undefined, type: 1, value: element.value })
                        count++
                    });
                }

                if (payload.success?.urlsList) {
                    let count = 0
                    payload.success?.urlsList?.forEach(element => {
                        r.urls.push({ key: count + "", label: element.id + "", type: 2, value: element.url })
                        count++
                    });

                }

                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach(element => {
                        r.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }

                state.item = r
            }
        })
        builder.addCase(get.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(get.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
    }
})

export const { reset, dismissMessage, resetUsers, setIsFilteredOwnersSet, setSelectedUserItems, resetItem, updateConnection, updateItem } = businessDetailsPageSlice.actions

export default businessDetailsPageSlice.reducer